import { FilterPills, Input, SegmentedControl } from '@aecom-ai-engineering/react-components';
import { LayoutGrid, List, Search as SearchIcon } from 'lucide-react';
import { type ChangeEvent, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CREATOR_FILTER, type CreatorFilter } from './use-project-list';
import { PROJECT_VIEW, type ProjectView } from './use-project-view';

interface ProjectListToolbarProps {
  creatorFilter: CreatorFilter;
  onCreatorFilterChange: (filter: CreatorFilter) => void;
  view: ProjectView;
  onViewChange: (view: ProjectView) => void;
  searchText: string;
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Filters on the left, search and the view switcher on the right, per the
 * design's 1136px row.
 *
 * `deselectable` is left alone: both components already default it to false,
 * which is what an explicit "All" wants — clicking the active pill should do
 * nothing rather than clear the row.
 *
 * "Created by others" is absent because the backend filter cannot express it.
 * `created_by` is a UUIDInFilter with no negative arm, so excluding a creator
 * would mean sending every other user's id.
 */
export const ProjectListToolbar: FC<ProjectListToolbarProps> = ({
  creatorFilter,
  onCreatorFilterChange,
  view,
  onViewChange,
  searchText,
  onSearch,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-4 py-6">
      <FilterPills
        items={[
          { label: t('project-list.filter-all'), value: CREATOR_FILTER.ALL },
          { label: t('project-list.filter-mine'), value: CREATOR_FILTER.MINE },
        ]}
        label={t('project-list.filter-label')}
        onValueChange={({ value }) =>
          onCreatorFilterChange(
            value[0] === CREATOR_FILTER.MINE ? CREATOR_FILTER.MINE : CREATOR_FILTER.ALL,
          )
        }
        value={[creatorFilter]}
      />

      <div className="flex items-center gap-2">
        {/* w-60 is the design's 240px; Input puts className on its shell.
            type="search" keeps the browser's native clear affordance. */}
        <Input
          className="w-60"
          icon={SearchIcon}
          label={t('project-list.search-projects')}
          onChange={onSearch}
          placeholder={t('project-list.search-projects')}
          size="md"
          type="search"
          value={searchText}
        />

        {/* data-project-view-switcher scopes the sliding highlight in index.css
            to this control. */}
        <SegmentedControl
          data-project-view-switcher=""
          items={[
            { icon: LayoutGrid, label: t('project-list.view-grid'), value: PROJECT_VIEW.GRID },
            { icon: List, label: t('project-list.view-list'), value: PROJECT_VIEW.LIST },
          ]}
          label={t('project-list.view-label')}
          onValueChange={({ value }) =>
            onViewChange(value[0] === PROJECT_VIEW.LIST ? PROJECT_VIEW.LIST : PROJECT_VIEW.GRID)
          }
          value={[view]}
        />
      </div>
    </div>
  );
};
